/**
 * Weirwood
 * Copyright 2019 Baidu Inc. All rights reserved.
 *
 * @file 一些帮助函数
 * @author zengxiaohui(zengxiaohui@baidu.com)
 */

import {htmlTreeAsString} from '../utils/misc';
import {Weirwood, Behavior} from '../interface/options.interface';

const debounceDuration = 1000;
let keypressTimeout: number | undefined;
let lastCapturedEvent: Event | undefined;

/**
 * 监听 `addEventListener` 点击事件
 * @param client 当前主实例
 * @param eventName 事件名称，例如 `click`
 * @returns 封装后的事件处理函数
 */
export function breadcrumbEventHandler(client: Weirwood, eventName: string): (event: Event) => void {
    return (event: Event) => {
        // reset keypress timeout; e.g. triggering a 'click' after
        // a 'keypress' will reset the keypress debounce so that a new
        // set of keypresses can be recorded
        keypressTimeout = undefined;

        // It's possible this handler might trigger multiple times for the same
        // event (e.g. event propagation through node ancestors). Ignore if we've
        // already captured the event.
        if (lastCapturedEvent === event) {
            return;
        }

        lastCapturedEvent = event;

        // try/catch both:
        // - accessing event.target (see getsentry/raven-js#838, #768)
        // - `htmlTreeAsString` because it's complex, and just accessing the DOM incorrectly
        //   can throw an exception in some circumstances.
        let target;
        try {
            target = htmlTreeAsString(event.target as Node);
        } catch (e) {
            target = '<unknown>';
        }

        // target 路径存在为空的情况，需要过滤掉
        if (target) {
            const behavior: Behavior = {
                category: `ui.${eventName}`, // e.g. ui.click, ui.input
                message: target, // DOM路径，例如 '"body > div > button#promise-error'
                name: eventName // 事件名称，例如 'click'
            };

            // 如果是输入，记录输入的值
            if (eventName === 'input') {
                // 如果是密码，为了安全性，不进行记录
                if ((target as any).type !== 'password') {
                    behavior.data = (event.target as HTMLInputElement).value || '';
                }
            }

            // 记录行为
            client.captureBehavior(behavior);
        }
    };
}

/**
 * 监听 `addEventListener` 键盘事件
 * @param client 当前主实例
 * @returns 封装后的事件处理函数
 */
export function keypressEventHandler(client: Weirwood): (event: Event) => void {
    // TODO: if somehow user switches keypress target before
    //       debounce timeout is triggered, we will only capture
    //       a single breadcrumb from the FIRST target (acceptable?)
    return (event: Event) => {
        let target;

        try {
            target = event.target;
        } catch (e) {
            // just accessing event properties can throw an exception in some rare circumstances
            // see: https://github.com/getsentry/raven-js/issues/838
            return;
        }

        const tagName = target && (target as HTMLElement).tagName;

        // only consider keypress events on actual input elements
        // this will disregard keypresses targeting body (e.g. tabbing
        // through elements, hotkeys, etc)
        if (
            !tagName ||
            (tagName !== 'INPUT' &&
                tagName !== 'TEXTAREA' &&
                !(target as HTMLElement).isContentEditable)
        ) {
            return;
        }

        // record first keypress in a series, but ignore subsequent
        // keypresses until debounce clears
        if (!keypressTimeout) {
            breadcrumbEventHandler(client, 'input')(event);
        }
        clearTimeout(keypressTimeout);

        keypressTimeout = (setTimeout(() => {
            keypressTimeout = undefined;
        }, debounceDuration) as any) as number;
    };
}
